import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { loadStripe, type PaymentIntent } from '@stripe/stripe-js';
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import settings from '../config/settings.json';
import { ENDPOINTS } from '../config';
import { STRIPE_PUBLISHABLE_KEY } from '../utils/env';
import { PaymentMethodIcon } from '../components/CardBrandIcon';
import { BrandBadge } from '../components/BrandBadge';
import { calculateProcessingFeeCents, processingFeeLabel, savedCardLabel } from '../utils/stripeFee';
import { formatDateDisplay } from '../utils/date';
import { trackEvent, getSessionId } from '../utils/analytics';
import { useLabels } from '../config/useLabels';
import { compareDueNow, isDueNow, parseCartInvoiceIds } from '../utils/cart';
import { checkoutPath } from '../utils/checkoutSource';
import { useCartHeldInvoiceIds } from '../hooks/useCartHeldInvoiceIds';
import { useLiveZelleCarts } from '../hooks/useLiveZelleCarts';

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

interface CartInvoice {
  id: string;
  total_amount: number;
  amount_paid: number;
  status: string;
  memo_code: string | null;
  due_date: string;
  payments: Array<{ status: string }>;
  studentName: string;
  programName: string;
  brandKey: string | null;
}

interface RawCartInvoice {
  id: string;
  total_amount: number;
  amount_paid: number;
  status: string;
  memo_code: string | null;
  due_date: string;
  enrollment: {
    metadata?: { level?: string } | null;
    student: { first_name: string; last_name: string } | { first_name: string; last_name: string }[] | null;
    program: { name: string; brand_key?: string | null } | { name: string; brand_key?: string | null }[] | null;
  } | null;
  payments: Array<{ status: string }> | null;
}

interface SavedCard { stripe_payment_method_id: string; brand: string | null; last4: string | null }
interface ConnectedBank { bank_name: string | null; last4: string | null }

/** cart-checkout's create response. */
interface CartQuote {
  cart_id: string;
  /** Card / ACH only; a Zelle cart has a memo code instead. */
  client_secret: string | null;
  payment_intent_id: string | null;
  memo_code: string | null;
  items: Array<{ invoice_id: string; balance_cents: number }>;
  subtotal_cents: number;
  fee_cents: number;
  fee_label: string | null;
  total_cents: number;
}

type Method = 'card' | 'bank_account' | 'zelle';

function usd(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

function one<T>(v: T | T[] | null | undefined): T | null {
  return Array.isArray(v) ? v[0] ?? null : v ?? null;
}

function balanceCents(inv: { total_amount: number; amount_paid: number }): number {
  return Math.max(0, Math.round((inv.total_amount - (inv.amount_paid ?? 0)) * 100));
}

/**
 * The cart and its checkout, on one page. Everything the family can pay now is listed and
 * ticked; untick to leave an invoice for later, or "Pay only this" for the usual one-invoice
 * checkout. The ticked invoices are paid in one card or ACH payment, or under one Zelle code.
 * Reached from the Payments page's "Due now" banner.
 */
export function CartCheckout() {
  const [searchParams] = useSearchParams();
  // A link may name the invoices to tick (?invoices=a,b); without one, everything is ticked.
  const requestedIds = useMemo(() => parseCartInvoiceIds(searchParams.get('invoices')), [searchParams]);
  const { user, isLoading: isAuthLoading } = useAuth();

  const { data: parentId } = useQuery<string | null>({
    queryKey: ['parent-id', user?.email],
    queryFn: async () => {
      const { data } = await supabase.from('parents').select('id').eq('email', user!.email!).maybeSingle();
      return (data?.id as string | undefined) ?? null;
    },
    enabled: !isAuthLoading && !!user?.email,
  });

  // Every invoice the family still owes something on; isDueNow below decides which can go in.
  const { data: openInvoices, error: invoicesError } = useQuery<CartInvoice[]>({
    queryKey: ['cart-open-invoices', parentId],
    queryFn: async () => {
      const { data: links, error: linkError } = await supabase
        .from('student_parents')
        .select('student_id')
        .eq('parent_id', parentId!)
        .eq('can_pay', true);
      if (linkError) throw linkError;
      const studentIds = ((links ?? []) as Array<{ student_id: string }>).map((l) => l.student_id);
      if (studentIds.length === 0) return [];
      const { data, error } = await supabase
        .from('invoices')
        .select(`
          id, total_amount, amount_paid, status, memo_code, due_date,
          enrollment:enrollments!inner (
            metadata,
            student:students ( first_name, last_name ),
            program:programs ( name, brand_key )
          ),
          payments ( status )
        `)
        .in('enrollment.student_id', studentIds)
        .in('status', ['pending', 'partial', 'overdue']);
      if (error) throw error;
      return ((data ?? []) as unknown as RawCartInvoice[]).map((row) => {
        const enrollment = one(row.enrollment);
        const student = one(enrollment?.student);
        const program = one(enrollment?.program);
        const level = enrollment?.metadata?.level;
        const name = program?.name ?? 'Program';
        return {
          id: row.id,
          total_amount: row.total_amount,
          amount_paid: row.amount_paid,
          status: row.status,
          memo_code: row.memo_code,
          due_date: row.due_date,
          payments: row.payments ?? [],
          studentName: student ? `${student.first_name} ${student.last_name}` : 'Student',
          programName: typeof level === 'string' && level.trim() ? `${name} (${level})` : name,
          brandKey: program?.brand_key ?? null,
        };
      });
    },
    enabled: !!parentId,
  });
  const heldByCart = useCartHeldInvoiceIds({ enabled: !!parentId });
  const liveZelle = useLiveZelleCarts(!!parentId);
  const dueNow = useMemo(
    () => (openInvoices ?? []).filter((inv) => isDueNow(inv, heldByCart, liveZelle)).sort(compareDueNow),
    [openInvoices, heldByCart, liveZelle],
  );

  const { data: savedCard } = useQuery<SavedCard | null>({
    queryKey: ['cart-saved-card', parentId],
    queryFn: async () => {
      const { data } = await supabase
        .from('card_payment_methods')
        .select('stripe_payment_method_id, brand, last4')
        .eq('parent_id', parentId!)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      return (data as SavedCard | null) ?? null;
    },
    enabled: !!parentId,
  });

  const { data: bank } = useQuery<ConnectedBank | null>({
    queryKey: ['cart-connected-bank', parentId],
    queryFn: async () => {
      const { data } = await supabase
        .from('bank_account_setup_intents')
        .select('bank_name, last4')
        .eq('parent_id', parentId!)
        .eq('is_active', true)
        .eq('status', 'succeeded')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      return (data as ConnectedBank | null) ?? null;
    },
    enabled: !!parentId,
  });

  // BHCS Cash and coupons apply to one invoice at a time, at the usual checkout. A family
  // with a balance is told so rather than left wondering where it went.
  const { data: cashCents } = useQuery<number>({
    queryKey: ['cart-cash-balance', parentId],
    queryFn: async () => {
      const { data: account } = await supabase.from('balance_accounts').select('id').eq('parent_id', parentId!).maybeSingle();
      if (!account?.id) return 0;
      const { data, error } = await supabase.rpc('get_balance', { p_account_id: account.id });
      if (error) return 0;
      return (data as Array<{ total_cents: number }> | null)?.[0]?.total_cents ?? 0;
    },
    enabled: !!parentId,
  });

  const loading = isAuthLoading || parentId === undefined || (!!parentId && openInvoices === undefined && !invoicesError);
  if (loading) {
    return (
      <div className="p-4" data-testid="loading-skeleton">
        <div className="max-w-2xl mx-auto animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="h-40 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (invoicesError) {
    return (
      <div className="p-4">
        <div className="max-w-2xl mx-auto bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded" role="alert">
          Couldn&apos;t load your invoices. <Link to="/payments" className="underline">Back to Payments</Link>
        </div>
      </div>
    );
  }

  if (dueNow.length === 0) {
    return (
      <div className="p-4">
        <div className="max-w-2xl mx-auto bg-white rounded-lg shadow p-6" data-testid="cart-empty">
          <h1 className="text-xl font-semibold text-gray-900">Nothing is due right now</h1>
          <p className="mt-2 text-sm text-gray-600">
            Invoices that are paid, or have a payment on the way, are not shown here.
          </p>
          <Link to="/payments" className="inline-block mt-4 text-sm font-medium text-blue-600 hover:underline">← Back to Payments</Link>
        </div>
      </div>
    );
  }

  // One invoice is never a bundle: it pays through its own checkout (BHCS Cash, coupons, its own
  // Zelle code all work there).
  if (dueNow.length === 1) return <Navigate to={checkoutPath(dueNow[0].id, 'due_now')} replace />;

  const initiallyLeftOut = requestedIds.length > 0
    ? dueNow.filter((inv) => !requestedIds.includes(inv.id)).map((inv) => inv.id)
    : [];
  return (
    <Elements stripe={stripePromise}>
      <CartCheckoutForm
        invoices={dueNow}
        initiallyLeftOut={initiallyLeftOut}
        savedCard={savedCard ?? null}
        bank={bank ?? null}
        cashCents={cashCents ?? 0}
      />
    </Elements>
  );
}

function CartCheckoutForm({ invoices, initiallyLeftOut, savedCard, bank, cashCents }: {
  invoices: CartInvoice[];
  initiallyLeftOut: string[];
  savedCard: SavedCard | null;
  bank: ConnectedBank | null;
  cashCents: number;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { user } = useAuth();
  const t = useLabels();

  const [method, setMethod] = useState<Method>('card');
  // Kept as what was left out, so an invoice that arrives while the page is open is in.
  const [leftOut, setLeftOut] = useState<Set<string>>(() => new Set(initiallyLeftOut));
  const [useSavedCard, setUseSavedCard] = useState(true);
  const [quote, setQuote] = useState<CartQuote | null>(null);
  const [priceChanged, setPriceChanged] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A connected bank is the cheaper default once it is known -- applied once, and never over a
  // method the parent already picked (the bank query can land late, or refetch on tab focus).
  const methodChosen = useRef(false);
  const defaultApplied = useRef(false);
  useEffect(() => {
    if (!bank || defaultApplied.current || methodChosen.current) return;
    defaultApplied.current = true;
    setMethod('bank_account');
  }, [bank]);

  const payable = invoices.filter((inv) => !leftOut.has(inv.id));
  const subtotalCents = payable.reduce((sum, inv) => sum + balanceCents(inv), 0);
  const chargedBrand = savedCard && useSavedCard ? savedCard.brand : null;
  const estimatedFeeCents = method === 'card' ? calculateProcessingFeeCents(subtotalCents, chargedBrand) : 0;
  const shownSubtotal = quote?.subtotal_cents ?? subtotalCents;
  const shownFee = quote ? quote.fee_cents : estimatedFeeCents;
  const shownTotal = quote?.total_cents ?? subtotalCents + estimatedFeeCents;
  const feeLabel = quote?.fee_label ?? processingFeeLabel(chargedBrand);

  // Any change to what would be charged throws away a quote the parent has not paid.
  const resetQuote = () => {
    setQuote(null);
    setPriceChanged(false);
    setError(null);
  };

  const toggle = (id: string) => {
    setLeftOut((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    resetQuote();
  };

  const createQuote = async (): Promise<CartQuote> => {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData.session?.access_token;
    if (!token) throw new Error('Please sign in again to pay.');
    const response = await fetch(ENDPOINTS.cartCheckout, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        action: 'create',
        invoice_ids: payable.map((inv) => inv.id),
        method,
        saved_payment_method_id: method === 'card' && savedCard && useSavedCard ? savedCard.stripe_payment_method_id : null,
      }),
    });
    const body = await response.json().catch(() => ({})) as Partial<CartQuote> & { message?: string };
    if (!response.ok) throw new Error(body.message || 'Could not start this payment. Please try again.');
    return body as CartQuote;
  };

  const confirm = async (q: CartQuote): Promise<PaymentIntent | undefined> => {
    if (!stripe || !elements || !q.client_secret) throw new Error('Payments are still loading. Please try again.');
    if (method === 'bank_account') {
      const result = await stripe.confirmPayment({
        clientSecret: q.client_secret,
        confirmParams: { return_url: `${window.location.origin}/cart/${q.cart_id}` },
        redirect: 'if_required',
      });
      if (result.error) throw new Error(result.error.message || 'Your bank payment could not be started.');
      return result.paymentIntent;
    }
    const result = savedCard && useSavedCard
      ? await stripe.confirmCardPayment(q.client_secret, { payment_method: savedCard.stripe_payment_method_id })
      : await stripe.confirmCardPayment(q.client_secret, { payment_method: { card: elements.getElement(CardElement)! } });
    if (result.error) {
      throw new Error(result.error.type === 'card_error'
        ? result.error.message || 'Your card was declined.'
        : result.error.message || 'Your card could not be charged.');
    }
    return result.paymentIntent;
  };

  const handlePay = async () => {
    setError(null);
    setIsPaying(true);
    let active = quote;
    try {
      if (method === 'zelle') {
        // Nothing to charge here: the next page shows the one memo code to send under.
        active = await createQuote();
        navigate(`/cart/${active.cart_id}`, { replace: true });
        return;
      }
      if (!active) {
        active = await createQuote();
        // Balances moved since this page loaded (a co-parent paid one, an admin adjusted
        // one): show the new total and take a second click before charging it.
        if (active.total_cents !== shownTotal) {
          setQuote(active);
          setPriceChanged(true);
          return;
        }
        setQuote(active);
      }
      const intent = await confirm(active);
      if (intent?.status === 'succeeded' || intent?.status === 'processing') {
        navigate(`/cart/${active.cart_id}`, { replace: true });
        return;
      }
      throw new Error(`Payment status: ${intent?.status ?? 'unknown'}. Please contact support if this persists.`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Payment failed. Please try again.';
      setError(message);
      if (active) {
        trackEvent({
          eventType: 'cart_payment_failed',
          user,
          entityType: 'payment_cart',
          entityId: active.cart_id,
          sessionId: getSessionId(),
          success: false,
          errorMessage: message,
          metadata: { cart_id: active.cart_id, method, total_cents: active.total_cents, invoice_count: active.items.length },
        });
      }
      // The next attempt opens a fresh cart. The server first checks this one with Stripe, so a
      // charge that went through after all is recorded and refused rather than charged again.
      setQuote(null);
      setPriceChanged(false);
    } finally {
      setIsPaying(false);
    }
  };

  const quotedBalance = (id: string, fallback: number) =>
    quote?.items.find((item) => item.invoice_id === id)?.balance_cents ?? fallback;

  return (
    <div className="p-4">
      <div className="max-w-2xl mx-auto">
        <Link to="/payments" className="inline-flex items-center text-sm font-medium text-blue-600 hover:underline mb-3">
          ← Back to Payments
        </Link>
        <div className="flex items-baseline justify-between gap-3 mb-6">
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">{t('label.dueNow')}</h1>
          {invoices.length > 1 && (
            <span className="text-sm text-gray-500" data-testid="cart-selected-count">
              {payable.length} of {invoices.length} selected
            </span>
          )}
        </div>

        <div className="bg-white rounded-lg shadow overflow-visible">
          <ul className="divide-y divide-gray-100" data-testid="cart-invoices">
            {invoices.map((inv) => {
              const included = !leftOut.has(inv.id);
              return (
                <li key={inv.id} className="flex items-start gap-3 p-4 sm:px-6">
                  <input
                    type="checkbox"
                    id={`cart-include-${inv.id}`}
                    className="mt-1 h-5 w-5 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    checked={included}
                    onChange={() => toggle(inv.id)}
                    aria-label={`Include ${inv.studentName}, ${inv.programName} in this payment`}
                    data-testid={`cart-include-${inv.id}`}
                  />
                  {/* Tapping the name ticks the box too. No per-invoice memo code here: a cart paid by
                      Zelle gets one code of its own, and a second code on screen invites the wrong one. */}
                  <label htmlFor={`cart-include-${inv.id}`} className="min-w-0 flex-1 cursor-pointer">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-gray-900">{inv.studentName}</span>
                      <BrandBadge brand={inv.brandKey} />
                    </span>
                    <span className="block text-sm text-gray-700">{inv.programName}</span>
                    <span className="block text-xs text-gray-500">Due {formatDateDisplay(inv.due_date)}</span>
                  </label>
                  <div className="shrink-0 text-right">
                    <div className={`font-semibold ${included ? 'text-gray-900' : 'text-gray-400'}`}>
                      {usd(quotedBalance(inv.id, balanceCents(inv)))}
                    </div>
                    {/* Always offered: it is also the way to use BHCS Cash or a coupon (see below). */}
                    <button
                      type="button"
                      className="mt-1 text-xs font-medium text-blue-600 hover:underline"
                      onClick={() => navigate(checkoutPath(inv.id, 'pay_only_this'))}
                      data-testid={`pay-only-${inv.id}`}
                    >
                      {t('action.payOnlyThis')}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
          {cashCents > 0 && (
            <p className="px-4 sm:px-6 py-3 text-xs text-gray-600 border-t border-gray-100 bg-emerald-50" data-testid="cart-cash-hint">
              You have {usd(cashCents)} in BHCS Cash. BHCS Cash and coupons are used one invoice at a time: choose{' '}
              <span className="font-medium">{t('action.payOnlyThis')}</span> on an invoice to use them.
            </p>
          )}

          <div className="p-4 sm:p-6 border-t border-gray-100 space-y-4">
            {payable.length === 1 ? (
              // One ticked invoice is not a bundle: hand it to its own checkout.
              <div className="space-y-3" data-testid="cart-single-step">
                <p className="text-sm text-gray-600">
                  One invoice is paid on its own checkout, where BHCS Cash and coupons also work.
                </p>
                <button
                  type="button"
                  onClick={() => navigate(checkoutPath(payable[0].id, 'pay_only_this'))}
                  className="w-full py-3 px-4 rounded-md text-white font-semibold bg-blue-600 hover:bg-blue-700"
                  data-testid="cart-single-continue"
                >
                  Continue to checkout · {usd(balanceCents(payable[0]))}
                </button>
              </div>
            ) : (
            <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {(['card', 'bank_account', 'zelle'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => { methodChosen.current = true; setMethod(m); resetQuote(); }}
                  className={`flex items-center gap-2.5 py-2.5 px-4 rounded-xl border text-left transition-colors ${
                    method === m ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <PaymentMethodIcon type={m} size={22} />
                  <span className="text-sm font-medium text-gray-900">
                    {m === 'card' ? 'Credit/Debit Card' : m === 'bank_account' ? 'Bank Account (ACH)' : 'Zelle®'}
                  </span>
                </button>
              ))}
            </div>

            {method === 'card' && (
              <div className="bg-gray-50 p-4 rounded-lg" data-testid="cart-card-section">
                {savedCard && useSavedCard ? (
                  <div className="flex items-center justify-between gap-3">
                    <span className="flex items-center gap-2 text-sm font-medium text-gray-900">
                      <PaymentMethodIcon type="card" brand={savedCard.brand} size={22} />
                      {savedCardLabel(savedCard.brand, savedCard.last4)}
                    </span>
                    <button type="button" className="text-sm text-blue-600 hover:underline" onClick={() => { setUseSavedCard(false); resetQuote(); }}>
                      Use a different card
                    </button>
                  </div>
                ) : (
                  <div>
                    {savedCard && (
                      <button type="button" className="mb-2 text-sm text-blue-600 hover:underline" onClick={() => { setUseSavedCard(true); resetQuote(); }}>
                        Use saved card ({savedCardLabel(savedCard.brand, savedCard.last4)})
                      </button>
                    )}
                    <CardElement className="p-3 border border-gray-300 rounded-md bg-white" options={{ style: { base: { fontSize: '16px' } } }} />
                  </div>
                )}
              </div>
            )}

            {method === 'bank_account' && (
              <div className="bg-gray-50 p-4 rounded-xl text-sm text-gray-600" data-testid="cart-bank-section">
                {bank ? (
                  <>
                    <p className="font-medium text-gray-900">{bank.bank_name ?? 'Bank'} •••• {bank.last4 ?? '••••'}</p>
                    <p className="mt-1">
                      One ACH debit for all {payable.length} invoices, usually 3–5 business days. They show as processing until it clears.
                    </p>
                  </>
                ) : (
                  <p>
                    Connect a bank account on your <Link to="/account/add-payment-method" className="text-blue-600 underline">Account</Link> page
                    to pay by ACH, or pay by card.
                  </p>
                )}
              </div>
            )}

            {method === 'zelle' && (
              <div className="bg-gray-50 p-4 rounded-xl text-sm text-gray-600" data-testid="cart-zelle-section">
                <p>
                  You&apos;ll get one memo code for all {payable.length} invoices. Send the total by Zelle to {settings.supportEmail}
                  with that code as the memo. No fee.
                </p>
                <p className="mt-1">
                  If your bank limits how much you can send in a day, you can send it over several days with the same code.
                </p>
              </div>
            )}

            <div className="bg-gray-50 p-4 rounded space-y-2 text-sm" data-testid="cart-summary">
              <div className="flex justify-between">
                <span className="text-gray-600">Invoices ({payable.length})</span>
                <span className="font-medium">{usd(shownSubtotal)}</span>
              </div>
              {shownFee > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Processing fee ({feeLabel}), once for the whole payment</span>
                  <span className="font-medium">{usd(shownFee)}</span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-gray-200">
                <span className="font-semibold">Total</span>
                <span className="font-bold">{usd(shownTotal)}</span>
              </div>
            </div>

            {payable.length === 0 && (
              <p className="text-sm text-gray-600" data-testid="cart-nothing-selected">Tick at least one invoice to pay.</p>
            )}
            {priceChanged && (
              <div className="text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded p-3" role="status">
                An invoice changed since you opened this page. The total is now {usd(shownTotal)}. Review it and pay again to confirm.
              </div>
            )}
            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3" role="alert">{error}</div>
            )}

            <button
              type="button"
              onClick={handlePay}
              disabled={isPaying || (method !== 'zelle' && !stripe) || payable.length === 0 || (method === 'bank_account' && !bank)}
              className="w-full py-3 px-4 rounded-md text-white font-semibold bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="cart-pay-button"
            >
              {isPaying ? 'Processing…' : method === 'zelle' ? `Get Zelle code for ${usd(shownTotal)}` : `${t('action.pay')} ${usd(shownTotal)}`}
            </button>
            </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
